import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useApi } from '../api/service';
import type { AllocationsResponse, ClaimRequest, TotalResponse } from './types';
import { errorHandler } from '@/lib/utils/error-handler';
import type { HashResponse } from '@/lib/types/hash.type';

export const useAllocations = (account: `0x${string}` | undefined) => {
  const { api } = useApi();
  const queryKey = ['/allocations', account];
  const allocations = useInfiniteQuery<AllocationsResponse, Error>({
    queryKey,
    queryFn: async ({ pageParam = undefined }) => {
      try {
        const response = await api.get<AllocationsResponse>('/allocations', {
          params: { pageKey: pageParam, account: account?.length ? account : undefined },
        });
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
    getNextPageParam: (page) => page.pageKey || undefined,
    initialPageParam: undefined,
    staleTime: 30000,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    enabled: Boolean(account),
  });
  return { allocations, queryKey };
};

export const useClaim = () => {
  const { api } = useApi();
  const claim = useMutation<HashResponse, Error, ClaimRequest>({
    mutationFn: async (dto: ClaimRequest) => {
      try {
        const response = await api.post<HashResponse>('/allocations/claim', dto);
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
  });
  return { claim };
};

export const useTotal = () => {
  const { api } = useApi();
  const total = useQuery<TotalResponse, Error>({
    queryKey: ['/allocations/total'],
    queryFn: async () => {
      try {
        const response = await api.get<TotalResponse>('/allocations/total');
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
    refetchOnWindowFocus: true,
    staleTime: 20000,
    refetchInterval: 20000,
  });
  return { total };
};
